/**
 * coin-pusher.js — GDD 6.7.2.
 *
 * A glass-fronted pusher machine. A platform sweeps back and forth at 0.5 Hz
 * over a tray already loaded with coins and prize tokens; the player drops
 * their own coins in through a chute and watches the pile creep toward the
 * front lip. Coins that go over the lip are counted. That count IS the score
 * (GDD 7.3 gives this stall a 0–100 coins-off-edge curve) and it picks the
 * prize tier. Coins are game pieces, never currency — nothing here touches the
 * ticket wallet beyond the entry fee.
 *
 * **The body budget is the real design constraint.** A hundred dropped coins on
 * top of a pre-loaded field would sit at ~140 dynamic bodies on a software
 * renderer, which this machine will not do at frame rate. So:
 *
 *   - coins are small cylinders with CCD off and sleeping left on
 *   - a counted coin is despawned the frame it crosses the lip
 *   - anything into a side gutter is despawned and never counted
 *   - anything stranded behind the pusher is merged away after a few seconds
 *   - `maxCoinBodies` is a hard ceiling: over it, the oldest coins at the back
 *     of the field quietly go
 *
 * The lip "sensor" is a plane test in `_scanCoins`, not a Rapier sensor volume.
 * Collision events on a hundred coins cost more than one transform read each,
 * and the plane test is exactly reproducible, which is what makes the tier
 * thresholds testable.
 *
 * Tuning lives in constants.js COIN_PUSHER.
 *
 * @module stalls/coin-pusher
 */

import * as THREE from 'three';
import { COIN_PUSHER, EVENTS, PALETTE } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import { createStallLight } from '../world/lighting.js';
import { grab } from '../core/grab.js';
import { bus } from '../core/events.js';
import { economy } from '../core/save.js';
import { haptics } from '../core/haptics.js';
import { randRange } from '../core/testmode.js';

const CFG = COIN_PUSHER;

const _v = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();

class CoinPusher extends StallBase {
  constructor(ctx) {
    super(ctx);
    /** @type {Array<{handle: object, kind: string, still: number, localZ: number}>} */
    this.coins = [];
    /** @type {object|null} The pusher's kinematic body. */
    this.pusher = null;
    /** @type {object|null} The one grabbable coin waiting in the dispenser. */
    this.dispenserCoin = null;
    /** @type {THREE.Object3D[]|THREE.Material[]} Anything allocated by hand. */
    this._disposables = [];

    this.phase = 'warmup';
    this.elapsed = 0;
    this.coinsOff = 0;
    this.tokensOff = 0;
    this.coinsLeft = CFG.coinsPerRound;
    this.tier = null;
    this.bonusTokens = 0;

    this._dispenserTimer = 0;
    this._clinkTimer = 0;
    this._invPlot = new THREE.Matrix4();
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    this._buildCabinet();
    this._buildField();
    this._buildChute();

    const sign = factory.createSign('', {
      w: 1.4, h: 0.44,
      lines: ['PUSH THE COINS OFF THE EDGE'],
      bg: '#12172a',
    });
    // 1.62, not 1.86: the scoreboard below spans y 1.935–2.785, and at 1.86
    // this banner's top edge cut off its bottom row.
    sign.position.set(0, 1.62, CFG.zBack + 0.06);
    sign.rotation.y = Math.PI;
    this.root.add(sign);

    this.buildScoreboard({ y: 2.36, z: CFG.zBack + 0.1 });

    const light = createStallLight(0xffe9b8, 2.4, 8);
    light.position.set(0, 2.1, 1.3);
    this.root.add(light);

    // Cabinet interior lamp, inside the case and just under the 1.14 m side
    // walls: the booth roof shadows the machine and the stall light is outside
    // it, so the tray read as a dark slot full of unlit coins. Decay 2 means
    // illuminance is intensity/d², so 0.18 at 0.34 m puts ~1.5 on the middle of
    // the tray and ~0.5 at its corners; `distance` 1.5 stops it well short of
    // the midway. On `this.root`, which teardown empties, so nothing leaks.
    const interior = createStallLight(0xffd9a0, 0.18, 1.5);
    interior.name = 'coin-pusher-interior';
    interior.position.set(0, CFG.fieldY + 0.34, (CFG.zFront + CFG.zBack) / 2);
    this.root.add(interior);
  }

  /**
   * Mesh + fixed body in one go, tracking anything we allocated ourselves so
   * teardown can give the GPU memory back.
   * @private
   */
  _solid(geometry, material, local, halfExtents, desc = {}) {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    this._disposables.push(geometry);
    this.spawn(mesh, local, {
      type: 'fixed', shape: 'box', halfExtents,
      position: this.localToWorld(local.x, local.y, local.z),
      ...desc,
    });
    return mesh;
  }

  /** Plinth, side walls, back wall, glass front, marquee. @private */
  _buildCabinet() {
    const halfW = 0.42;
    const baseTop = CFG.fieldY - 0.06;
    const front = CFG.cabinetFrontZ;
    const zMid = (front + CFG.zBack) / 2;
    const depth = CFG.zBack - front;

    // Two blocks, not one: the tall part carries the tray and stops at the
    // front lip, and a lower plinth carries the section in front of it. The
    // step between them is the payout well. One full-depth block instead would
    // fill that well in with solid wood and there would be nowhere to see the
    // coins land.
    const wellFloor = 0.5;
    const bodyDepth = CFG.zBack - CFG.zFront;
    this._solid(
      new THREE.BoxGeometry(halfW * 2, baseTop, bodyDepth),
      factory.mat(PALETTE.wood, { rough: 0.9 }),
      { x: 0, y: baseTop / 2, z: (CFG.zFront + CFG.zBack) / 2 },
      { x: halfW, y: baseTop / 2, z: bodyDepth / 2 },
      { friction: 0.8 },
    );
    this._solid(
      new THREE.BoxGeometry(halfW * 2, wellFloor, CFG.zFront - front),
      factory.mat(PALETTE.wood, { rough: 0.9 }),
      { x: 0, y: wellFloor / 2, z: (front + CFG.zFront) / 2 },
      { x: halfW, y: wellFloor / 2, z: (CFG.zFront - front) / 2 },
      { friction: 0.8 },
    );

    for (const side of [-1, 1]) {
      this._solid(
        new THREE.BoxGeometry(0.04, 0.42, depth),
        factory.mat(PALETTE.boothFrame, { rough: 0.8 }),
        { x: side * halfW, y: baseTop + 0.21, z: zMid },
        { x: 0.02, y: 0.21, z: depth / 2 },
      );
    }

    // Backboard. It fills the middle of the player's view and 0x243447 under a
    // roofed booth tone-mapped to near-black — a hole where the machine's back
    // should be. Same slate family, lifted about two stops so it reads as a
    // surface the interior lamp is falling on.
    this._solid(
      new THREE.BoxGeometry(halfW * 2, 0.5, 0.05),
      factory.mat(0x4a5f79, { rough: 0.7 }),
      { x: 0, y: baseTop + 0.25, z: CFG.zBack },
      { x: halfW, y: 0.25, z: 0.025 },
    );

    // Glass. No collider: the player reaches in over the top, and a collider
    // here would only fight the grab system.
    const glassMat = new THREE.MeshStandardMaterial({
      color: 0xbfe4f0, roughness: 0.05, metalness: 0.1,
      transparent: true, opacity: 0.16, depthWrite: false,
    });
    // Stops below the chute: the chute crosses this plane at about y 1.14, and
    // a taller pane would have the rails clipping straight through the glass.
    const glassGeo = new THREE.BoxGeometry(halfW * 2 - 0.06, 0.4, 0.012);
    const glass = new THREE.Mesh(glassGeo, glassMat);
    glass.position.set(0, baseTop + 0.15, front);
    this.root.add(glass);
    this._disposables.push(glassGeo, glassMat);
  }

  /** Field floor, side rails, back wall, and the hanging baffle. @private */
  _buildField() {
    const w = CFG.fieldHalfWidth;
    const depth = CFG.zBack - CFG.zFront;
    const zMid = (CFG.zFront + CFG.zBack) / 2;

    this._solid(
      new THREE.BoxGeometry(w * 2, 0.06, depth),
      factory.mat(0x8f9aa6, { rough: 0.55, metal: 0.3 }),
      { x: 0, y: CFG.fieldY - 0.03, z: zMid },
      { x: w, y: 0.03, z: depth / 2 },
      { friction: 0.35 },
    );

    // Low rails: high enough to keep the pile tidy, low enough that a coin
    // shoved sideways by the pile really can go over into the gutter.
    for (const side of [-1, 1]) {
      this._solid(
        new THREE.BoxGeometry(0.02, 0.03, depth),
        factory.mat(PALETTE.metal, { rough: 0.4, metal: 0.6 }),
        { x: side * (w + 0.01), y: CFG.fieldY + 0.015, z: zMid },
        { x: 0.01, y: 0.015, z: depth / 2 },
      );
    }

    // Back wall and baffle, lifted with the backboard: these two sit directly
    // behind the coins, and at 0x3d4a58 they were dark enough that a coin at
    // the back of the tray had nothing to be a silhouette against.
    this._solid(
      new THREE.BoxGeometry(w * 2, 0.2, 0.03),
      factory.mat(0x5d6e80, { rough: 0.8 }),
      { x: 0, y: CFG.fieldY + 0.1, z: CFG.zBack },
      { x: w, y: 0.1, z: 0.015 },
    );

    // Hangs above the pusher's top so the pusher sweeps underneath it.
    this._solid(
      new THREE.BoxGeometry(w * 2, 0.16, 0.02),
      factory.mat(0x5d6e80, { rough: 0.8 }),
      { x: 0, y: CFG.fieldY + 0.16, z: CFG.baffleZ },
      { x: w, y: 0.08, z: 0.01 },
    );
  }

  /**
   * The slot guide. The collider is one flat plate; the visible part is a pair
   * of rails so the chute does not become a wall across the player's view.
   * @private
   */
  _buildChute() {
    const m = CFG.chuteMouth;
    const e = CFG.chuteExit;
    const dz = e.z - m.z;
    const dy = e.y - m.y;
    const len = Math.hypot(dz, dy);
    const pitch = Math.atan2(-dy, dz);
    const mid = { x: 0, y: (m.y + e.y) / 2, z: (m.z + e.z) / 2 };

    _e.set(pitch, 0, 0);
    const worldRot = this.worldQuaternion().multiply(_q.setFromEuler(_e));

    const plateGeo = new THREE.BoxGeometry(CFG.chuteHalfWidth * 2, 0.02, len);
    const plate = new THREE.Mesh(plateGeo, factory.mat(PALETTE.metal, { rough: 0.2, metal: 0.9 }));
    plate.visible = false;
    plate.rotation.x = pitch;
    this._disposables.push(plateGeo);
    this.spawn(plate, mid, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: CFG.chuteHalfWidth, y: 0.01, z: len / 2 },
      position: this.localToWorld(mid.x, mid.y, mid.z),
      rotation: worldRot,
      friction: CFG.chuteFriction, restitution: 0.02,
    });

    const rails = new THREE.Group();
    const railGeo = new THREE.BoxGeometry(0.02, 0.03, len);
    const railMat = factory.mat(PALETTE.gold, { rough: 0.3, metal: 0.9 });
    this._disposables.push(railGeo);
    for (const side of [-1, 1]) {
      const rail = new THREE.Mesh(railGeo, railMat);
      rail.position.x = side * CFG.chuteHalfWidth;
      rails.add(rail);
    }
    const barGeo = new THREE.BoxGeometry(CFG.chuteHalfWidth * 2, 0.008, 0.014);
    this._disposables.push(barGeo);
    for (let i = 0; i <= 4; i++) {
      const bar = new THREE.Mesh(barGeo, railMat);
      bar.position.z = -len / 2 + (i / 4) * len;
      bar.position.y = -0.008;
      rails.add(bar);
    }
    rails.position.set(mid.x, mid.y, mid.z);
    rails.rotation.x = pitch;
    this.root.add(rails);

    // Cup the dispensed coin sits in, purely so it does not look like it is
    // floating in mid-air.
    const cupGeo = new THREE.CylinderGeometry(0.035, 0.03, 0.012, 12);
    const cup = new THREE.Mesh(cupGeo, factory.mat(PALETTE.gold, { rough: 0.35, metal: 0.85 }));
    const d = CFG.dispenserPos;
    cup.position.set(d.x, d.y - 0.018, d.z);
    this.root.add(cup);
    this._disposables.push(cupGeo);
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({
      seconds: CFG.warmupSeconds + CFG.activeSeconds + CFG.settleSeconds,
    });
    this.coins = [];
    this.phase = 'warmup';
    this.elapsed = 0;
    this.coinsOff = 0;
    this.tokensOff = 0;
    this.coinsLeft = CFG.coinsPerRound;
    this.tier = null;
    this.bonusTokens = 0;
    this.dispenserCoin = null;
    this._dispenserTimer = 0;
    this.pusherZ = CFG.pusherHomeZ;

    this._spawnPusher();
    this._preloadField();
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'machine-hum', loop: true, volume: 0.35 });
  }

  /** @private */
  _spawnPusher() {
    const geo = new THREE.BoxGeometry(CFG.pusherWidth, CFG.pusherHeight, CFG.pusherDepth);
    this._disposables.push(geo);
    const mesh = new THREE.Mesh(geo, factory.mat(0xb8c2cc, { rough: 0.35, metal: 0.55 }));
    mesh.castShadow = true;

    // Centre sits at field level so the top stands 0.05 m proud of the tray and
    // the lower half tucks inside the floor. Two non-dynamic bodies never
    // generate contacts, so the overlap costs nothing.
    this.pusher = this.spawn(mesh, { x: 0, y: CFG.fieldY, z: CFG.pusherHomeZ }, {
      type: 'kinematic', shape: 'box',
      halfExtents: {
        x: CFG.pusherWidth / 2, y: CFG.pusherHeight / 2, z: CFG.pusherDepth / 2,
      },
      position: this.localToWorld(0, CFG.fieldY, CFG.pusherHomeZ),
      friction: 0.3, restitution: 0,
    });
  }

  /**
   * Lay the tray out already loaded (GDD 6.7.2). Coins go down in a grid a
   * millimetre above the tray and settle during the warm-up, which is both
   * cheaper and far more stable than dropping thirty-five bodies from height.
   * @private
   */
  _preloadField() {
    const g = CFG.fieldGrid;
    const total = CFG.fieldCoins + CFG.fieldTokens;
    const tokenSlots = new Set();
    // Spread the tokens through the middle of the pile rather than the front
    // row, so they are a target rather than a freebie.
    for (let i = 0; i < CFG.fieldTokens; i++) {
      tokenSlots.add(g.cols + 2 + i * Math.floor((total - g.cols - 2) / CFG.fieldTokens));
    }

    for (let i = 0; i < total; i++) {
      const col = i % g.cols;
      const row = Math.floor(i / g.cols);
      const x = (col - (g.cols - 1) / 2) * g.spacingX + randRange(-0.004, 0.004);
      const z = g.z0 + row * g.spacingZ + randRange(-0.004, 0.004);
      this._spawnCoin(
        { x, y: CFG.fieldY + 0.006, z },
        tokenSlots.has(i) ? 'token' : 'coin',
      );
    }
  }

  /**
   * @param {{x:number,y:number,z:number}} local
   * @param {'coin'|'token'} kind
   * @returns {object} The tracking entry.
   * @private
   */
  _spawnCoin(local, kind) {
    const isToken = kind === 'token';
    const mesh = isToken ? factory.createToken() : factory.createCoin();
    const handle = this.spawn(mesh, local, {
      shape: 'cylinder',
      radius: isToken ? 0.022 : 0.019,
      halfHeight: isToken ? 0.002 : 0.00125,
      mass: isToken ? 0.012 : CFG.coinMass,
      friction: CFG.coinFriction,
      restitution: CFG.coinRestitution,
      ccd: false,
      angularDamping: 0.5,
      linearDamping: 0.15,
      position: this.localToWorld(local.x, local.y, local.z),
    });
    handle.userData.coinKind = kind;
    const entry = { handle, kind, still: 0, localZ: local.z };
    this.coins.push(entry);
    return entry;
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    // Same guard as Hook a Duck: the manager is already "active" during the
    // first-person blink, so this runs before setupRound() has spawned the
    // pusher. Without it the round clock starts ticking during the transition.
    if (!this.pusher) return;

    this.elapsed += dt;
    this._clinkTimer = Math.max(0, this._clinkTimer - dt);
    this._updatePhase();
    this._updatePusher();
    this._scanCoins(dt);
    this._updateDispenser(dt);
    this._enforceCap();
  }

  /** @private */
  _updatePhase() {
    const active = CFG.warmupSeconds + CFG.activeSeconds;
    const next = this.elapsed < CFG.warmupSeconds ? 'warmup'
      : this.elapsed < active ? 'active' : 'settle';
    if (next === this.phase) return;
    this.phase = next;

    if (next === 'active') {
      this._dispenserTimer = 0;
    } else if (next === 'settle') {
      // No more coins in: the last 10 s are for the pile to finish falling.
      this._clearDispenser();
      bus.emit(EVENTS.UI_TOAST, { text: 'Settling…', kind: 'good', duration: 2000 });
    }
    this.updateScoreboard();
  }

  /**
   * Sine sweep at the GDD's 0.5 Hz.
   *
   * `setNextKinematicTranslation` rather than `setPosition`: Rapier derives a
   * kinematic body's velocity from where it is heading next, and that velocity
   * is what actually shoves the coins. Teleporting the body would let the pile
   * tunnel straight through it.
   * @private
   */
  _updatePusher() {
    if (!this.pusher || this.pusher.removed) return;
    const sweep = 1 - Math.cos(2 * Math.PI * CFG.pusherHz * this.elapsed);
    const z = CFG.pusherHomeZ - (CFG.pusherStroke / 2) * sweep;
    this.pusherZ = z;
    const p = this.localToWorld(0, CFG.fieldY, z);
    this.pusher.body.setNextKinematicTranslation({ x: p.x, y: p.y, z: p.z });
  }

  /**
   * The lip sensor, the gutters, and the housekeeping, in one pass over the
   * coins. One inverse-matrix build per frame instead of one per coin.
   * @private
   */
  _scanCoins(dt) {
    this.plot.root.updateWorldMatrix(true, false);
    this._invPlot.copy(this.plot.root.matrixWorld).invert();
    const counting = this.phase !== 'warmup';
    let coins = 0;
    let tokens = 0;

    for (let i = this.coins.length - 1; i >= 0; i--) {
      const c = this.coins[i];
      if (c.handle.removed) {
        this.coins.splice(i, 1);
        continue;
      }

      const t = c.handle.body.translation();
      _v.set(t.x, t.y, t.z).applyMatrix4(this._invPlot);
      c.localZ = _v.z;

      if (_v.y < CFG.killPlaneY) {
        this._despawn(i);
        continue;
      }

      // Gutters are pure loss — only the FRONT edge pays (GDD 6.7.2). Once one
      // has dropped off the tray it is gone: it comes to rest on the cabinet
      // base beside the field, well above the kill plane, so without this it
      // would sit there costing a body for the rest of the round.
      const inGutter = Math.abs(_v.x) > CFG.gutterHalfWidth;
      if (inGutter && _v.y < CFG.fieldY - 0.03) {
        this._despawn(i);
        continue;
      }

      if (counting && !inGutter && _v.z < CFG.zFront && _v.y < CFG.countPlaneY) {
        if (c.kind === 'token') tokens += 1;
        else coins += 1;
        this._despawn(i);
        continue;
      }

      // Stranded: past the pusher's back face at full extension, so on the
      // return stroke it gets wedged against the back wall and never moves
      // again. A coin merely RIDING the pusher is travelling with it, so its
      // stillness counter keeps resetting and it is never caught by this.
      if (_v.z > CFG.pusherHomeZ - CFG.pusherStroke + CFG.pusherDepth / 2) {
        const v = c.handle.body.linvel();
        c.still = (v.x * v.x + v.y * v.y + v.z * v.z) < 1e-4 ? c.still + dt : 0;
        if (c.still > CFG.strandedSeconds) this._despawn(i);
      }
    }

    if (coins > 0 || tokens > 0) this._registerPayout(coins, tokens);
  }

  /** @param {number} index Index into `this.coins`. @private */
  _despawn(index) {
    const c = this.coins[index];
    this.coins.splice(index, 1);
    if (c && !c.handle.removed) c.handle.remove();
  }

  /** @private */
  _registerPayout(coins, tokens) {
    if (coins > 0) {
      this.coinsOff += coins;
      this.addScore(coins, {
        position: this.localToWorld(0, CFG.fieldY - 0.1, CFG.zFront - 0.1),
        label: coins > 1 ? `${coins} coins off` : 'coin off',
      });
    }
    if (tokens > 0) {
      this.tokensOff += tokens;
      bus.emit(EVENTS.UI_TOAST, {
        text: `Prize token! +${tokens * CFG.tokenBonusEach} tokens`,
        kind: 'prize',
        duration: 2200,
      });
      haptics.play('right', 'score');
    }
    if (this._clinkTimer <= 0) {
      bus.emit(EVENTS.AUDIO_PLAY, { name: 'coin-clink', volume: 0.6 });
      this._clinkTimer = 0.06;
    }
    this.updateScoreboard();
  }

  // ── Dispenser ───────────────────────────────────────────────────────────

  /** @private */
  _updateDispenser(dt) {
    if (this.phase !== 'active' || this.dispenserCoin || this.coinsLeft <= 0) return;
    this._dispenserTimer -= dt;
    if (this._dispenserTimer <= 0) this._spawnDispenserCoin();
  }

  /**
   * One coin at a time, kinematic so it sits in the cup until it is picked up.
   * `grab` flips it to dynamic on release, which is exactly the drop we want.
   * @private
   */
  _spawnDispenserCoin() {
    const d = CFG.dispenserPos;
    const mesh = factory.createCoin();
    const handle = this.spawn(mesh, d, {
      type: 'kinematic', shape: 'cylinder', radius: 0.019, halfHeight: 0.00125,
      mass: CFG.coinMass, friction: CFG.coinFriction,
      restitution: CFG.coinRestitution, ccd: false,
      position: this.localToWorld(d.x, d.y, d.z),
    });
    handle.userData.dispensed = true;
    grab.register(handle, {
      kind: 'coin',
      onRelease: () => this._onCoinDropped(handle),
    });
    this.dispenserCoin = handle;
  }

  /** @private */
  _onCoinDropped(handle) {
    if (this.dispenserCoin !== handle) return;
    this.dispenserCoin = null;
    this.coinsLeft = Math.max(0, this.coinsLeft - 1);

    // It is a live coin now, not a dispenser coin: stop it being re-grabbed and
    // start tracking it for the lip test.
    grab.unregister(handle);
    this.coins.push({ handle, kind: 'coin', still: 0, localZ: CFG.chuteMouth.z });
    this._dispenserTimer = CFG.dispenserDelay;
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'coin-drop', volume: 0.5 });
    this.updateScoreboard();
  }

  /** @private */
  _clearDispenser() {
    if (!this.dispenserCoin) return;
    grab.unregister(this.dispenserCoin);
    if (!this.dispenserCoin.removed) this.dispenserCoin.remove();
    this.dispenserCoin = null;
  }

  /**
   * Hard ceiling on live coin bodies. The ones that go are the oldest at the
   * BACK of the field — furthest from the lip, least visible, least in play.
   * @private
   */
  _enforceCap() {
    const over = this.coins.length - CFG.maxCoinBodies;
    if (over <= 0) return;
    const doomed = this.coins
      .filter((c) => c.kind === 'coin' && !c.handle.removed)
      .sort((a, b) => b.localZ - a.localZ)
      .slice(0, over);
    for (const c of doomed) {
      const i = this.coins.indexOf(c);
      if (i >= 0) this._despawn(i);
    }
  }

  // ── Round end ───────────────────────────────────────────────────────────

  /**
   * GDD 6.7.2 prize tiers by coins off the front edge, plus 5 redemption
   * tokens for every prize token pushed over. The score itself is the coin
   * count, which constants.js turns into the ordinary token payout.
   */
  onRoundEnd() {
    this._clearDispenser();
    const tier = CFG.tiers.find((t) => this.coinsOff <= t.max) || CFG.tiers[CFG.tiers.length - 1];
    this.tier = tier;
    this.bonusTokens = this.tokensOff * CFG.tokenBonusEach + tier.bonusTokens;

    if (this.bonusTokens > 0) {
      economy.grant({ tokens: this.bonusTokens }, `stall:${this.id}:bonus`);
    }
    this.awardDirectPrize(tier.prize);
    this._showPrize(tier.prize, tier.label);
  }

  /**
   * Stand the won prize on the machine. Placed at full size rather than
   * animated in: this runs from `onRoundEnd`, after the last `onUpdate` of the
   * round, so there is no frame hook left to grow it.
   * @private
   */
  _showPrize(kind, label) {
    const plush = factory.createPlush(kind);
    plush.position.set(0, CFG.fieldY + 0.3, CFG.zFront + 0.3);
    plush.scale.setScalar(kind === 'plush-large' ? 1.6 : 1);
    plush.rotation.y = Math.PI;
    this.root.add(plush);
    bus.emit(EVENTS.UI_TOAST, { text: label, kind: 'prize', duration: 3200 });
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    const label = this.phase === 'warmup' ? 'READY'
      : this.phase === 'settle' ? 'SETTLING' : `${Math.ceil(Math.max(0, this.timeLeft - CFG.settleSeconds))}s`;
    this.scoreboard.setLines([
      `Coins off  ${this.coinsOff}`,
      `Tokens     ${this.tokensOff}`,
      `Time       ${label}`,
      `In hand    ${this.coinsLeft}`,
    ]);
  }

  onTeardown() {
    this._clearDispenser();
    this.coins = [];
    this.pusher = null;
    this._prizeAnim = null;
    for (const d of this._disposables) d.dispose();
    this._disposables = [];
  }

  // ── Test automation (reached through __cc.stalls, never imported) ────────

  /** @returns {object} Snapshot for `__cc.stalls.debug()`. */
  debugState() {
    return {
      stallId: this.id,
      phase: this.phase,
      elapsed: this.elapsed,
      coinsOff: this.coinsOff,
      tokensOff: this.tokensOff,
      coinsLeft: this.coinsLeft,
      liveCoins: this.coins.length,
      bonusTokens: this.bonusTokens,
      tier: this.tier ? this.tier.label : null,
      hasDispenserCoin: !!this.dispenserCoin,
      /**
       * Where the sweep is DRIVING the pusher, in stall-local Z. Deliberately
       * the commanded value rather than the body's: a kinematic body's
       * translation only catches up on the next physics step, so reading the
       * body would report a frozen pusher whenever the clock is fast-forwarded.
       */
      pusherZ: this.pusherZ ?? null,
    };
  }

  /**
   * Stall-specific test hooks, routed from `__cc.stalls.action()`.
   * @param {string} name @param {object} args
   */
  testHook(name, args = {}) {
    if (name !== 'dropCoins') return null;
    return this.dropCoins(args.n ?? 1, args.xOffset ?? 0, args);
  }

  /**
   * Inject coins deterministically. Backs `__cc.stalls.dropCoins()`.
   *
   * `zone` picks where they enter:
   *   `slot`   above the chute mouth — the path a player's coin takes
   *   `edge`   already past the lip and below the count plane, so the very next
   *            scan counts them through the ordinary code path
   *   `gutter` out at the side, to prove a lost coin is never counted
   *
   * @param {number} n
   * @param {number} [xOffset] Lateral offset, metres.
   * @param {{zone?: string, token?: boolean}} [opts]
   * @returns {number} How many were spawned.
   */
  dropCoins(n = 1, xOffset = 0, opts = {}) {
    const zone = opts.zone || 'slot';
    const kind = opts.token ? 'token' : 'coin';
    const perRow = 11;

    for (let i = 0; i < n; i++) {
      const col = i % perRow;
      const row = Math.floor(i / perRow);
      let local;
      if (zone === 'edge') {
        local = {
          x: xOffset + (col - (perRow - 1) / 2) * 0.045,
          y: CFG.countPlaneY - 0.04 - row * 0.006,
          z: CFG.zFront - 0.12,
        };
      } else if (zone === 'gutter') {
        // Identical to `edge` but outside the gutter line, so the only thing
        // stopping these from counting is the rule under test.
        local = {
          x: (xOffset >= 0 ? 1 : -1) * (CFG.gutterHalfWidth + 0.04 + col * 0.01),
          y: CFG.countPlaneY - 0.04 - row * 0.006,
          z: CFG.zFront - 0.12,
        };
      } else {
        local = {
          x: xOffset + (col - (perRow - 1) / 2) * 0.02,
          y: CFG.chuteMouth.y + 0.05 + i * 0.012,
          z: CFG.chuteMouth.z + 0.08,
        };
      }
      this._spawnCoin(local, kind);
    }
    return n;
  }
}

registerStall({
  id: 'coin-pusher',
  name: 'Coin Pusher',
  cost: CFG.cost,
  create: (ctx) => new CoinPusher(ctx),
});

export { CoinPusher };
